import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import * as d3 from 'd3';
import moment from 'moment';
import { getDateRangeArray, getWeekRangeByMonth } from '@/utils/date';
import { MONTHS, ReportDefaultColor, ReportTypeEnum } from '@/utils/constants';
import { formatDuration, secondsToMinutes } from '@/utils/utils';
import { appConfigService } from '@/providers/services/app/app-config';
@Component({
    name: 'chart-card',
})
export default class ChartCardComponent extends Vue {

    @Prop()
    public dataList: any;

    @Prop({ default: 'daily' })
    public frequency!: string;

    @Prop()
    public activeMonth!: string;

    @Prop()
    public activeYear!: string;

    @Prop()
    public userType!: string;

    @Prop({ default: false })
    public showAverageSessionChart!: boolean;

    @Prop({ default: false })
    public showNudgeTimeSpentChart!: boolean;

    public tooltipStyle: any = { toolX: 0, toolY: 0 };

    public tooltipData: any = null;

    public chartWidth = 1060;

    public chartHeight = 420;

    public margin = { top: 20, right: 30, bottom: 50, left: 60 };

    @Prop({ default: () => [] })
    public selectedActiveUserIds!: string[];

    @Prop({ default: () => [] })
    public selectedSessionUsageUserIds!: string[];

    private get startDate() {
        return moment([this.activeYear, parseInt(this.activeMonth, 10) - 1]).toDate();
    }

    private get endDate() {
        return (this.activeMonth === moment().format('MM')
            && this.activeYear === moment().format('YYYY'))
            ? moment().subtract(1, 'day').toDate()
            : moment(this.startDate).endOf('month').toDate();
    }

    public get isCurrentMonthActive() {
        return this.activeMonth === moment().format('MM') && this.activeYear === moment().format('YYYY');
    }

    public get dailyFrequencyLabels() {
        return getDateRangeArray(
            moment(this.startDate).format('YYYY-MM-DD'),
            moment(this.endDate).format('YYYY-MM-DD'));
    }

    private get weeklyFrequencyLabels() {
        const tillCurrentWeek = this.activeMonth === moment().format('MM')
            && this.activeYear === moment().format('YYYY');
        return getWeekRangeByMonth(this.activeMonth, this.activeYear, tillCurrentWeek);
    }

    public get reportUserType() {
        return appConfigService.getReportUserType();
    }

    @Watch('dataList', { deep: true })
    public async onChangeData() {
        const groupedData = this.groupByUserType();
        this.drawChart(groupedData);
    }

    // -----------------------------------------------------------------
    // Hooks
    public mounted() {
        const response = this.groupByUserType();
        this.drawChart(response);
    }

    // ------------------------------------------------------------------
    // Methods
    public drawChart(data: any) {
        d3.select(this.$el).select('.line-chart-container').select('svg').remove();
        const chartData: Record<string, any> = {};

        if (data) {
            for (const key in data) {
                if (data.hasOwnProperty(key) && key !== 'undefined') {
                    chartData[key] = this.parseChartData(data[key]);
                }
            }
        }
        const width =
            this.chartWidth - this.margin.left - this.margin.right;
        const height =
            this.chartHeight - this.margin.top - this.margin.bottom;


        const svg = d3.select(this.$el).select('.line-chart-container')
            .append('svg')
            .attr('width', this.chartWidth)
            .attr('height', this.chartHeight)
            .append('g')
            .attr(
                'transform',
                `translate(${this.margin.left},${this.margin.top})`,
            );

        // Check if any dataset contains data
        const hasData = Object.values(chartData).some((dataSet) => dataSet.length > 0);
        if (!hasData) {
            return this.displayNoDataMessage(svg);
        }

        // X-axis scale
        const allData = Object.values(chartData).flat();
        const xMinValue: number = d3.min(allData, (d: any) => Number(d.pos)) || 1;
        const xMaxValue: number = d3.max(allData, (d: any) => d.pos) || 1;

        const x: any = d3.scaleLinear()
            .domain([xMinValue - 0.5, xMaxValue + 0.5])
            .range([0, width]);

        // Y-axis scale
        const yMaxValue: number = d3.max(allData, (d: any) => {
            const value = Number(d.value);
            return value < 10 ? 10 : value;
        }) || 10;

        const y: any = d3.scaleLinear()
            .domain([0, yMaxValue <= 10 ? 10 : yMaxValue])
            .range([height, 0]);


        // Line generator
        const line: any = d3
            .line<any>()
            .x((d) => x(d.pos))
            .y((d) => y(d.value))
            .curve(d3.curveMonotoneX);
        const datasetColors: any = this.generateDatasetColors();

        // Create gradients dynamically
        Object.keys(chartData).forEach((key) => {
            const { line: color = ReportDefaultColor,
                gradient: gradientId = `gradient-${key}` } = datasetColors[key] || {};

            svg.append('defs')
                .append('linearGradient')
                .attr('id', gradientId)
                .attr('x1', '0%')
                .attr('y1', '0%')
                .attr('x2', '0%')
                .attr('y2', '100%')
                .selectAll('stop')
                .data([
                    { offset: '0%', color: `${color}29` },
                    { offset: '80%', color: `${color}29` },
                    { offset: '100%', color: 'rgba(255, 255, 255, 0)' },
                ])
                .enter()
                .append('stop')
                .attr('offset', (d) => d.offset)
                .attr('stop-color', (d) => d.color);
        });

        // Draw lines for each dataset
        Object.keys(chartData).forEach((key) => {
            const {
                line: color = ReportDefaultColor,
                gradient: gradientId = `gradient-${key}`,
            } = datasetColors[key] || {};

            svg.append('path')
                .datum(chartData[key])
                .attr('fill', this.showAverageSessionChart ? `url(#${gradientId})` : 'none')
                .attr('stroke', color)
                .attr('stroke-width', 3)
                .attr('d', line);
        });

        // X-axis
        svg.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(
                d3.axisBottom(x)
                    .tickValues(allData.map((item: any) => item.pos))
                    .tickFormat((d) => {
                        const tick = allData.find((item: any) => item.pos === d);
                        return tick ? tick.label : '';
                    }),
            )
            .selectAll('text')
            .style('font-size', '12px')
            .style('font-family', 'Montserrat');

        const tickValues = d3.range(0, yMaxValue + 1, yMaxValue / 10);

        // Y-axis
        svg.append('g')
            .call(d3.axisLeft(y).tickValues(tickValues).tickSize(5).tickPadding(5))
            .selectAll('text')
            .style('font-size', '12px')
            .style('font-family', 'Montserrat');


        // Grid lines for Y-axis
        svg.selectAll('.grid-line')
            .data(tickValues)
            .enter()
            .append('line')
            .attr('class', 'grid-line')
            .attr('x1', 0)
            .attr('x2', width)
            .attr('y1', (d) => y(d))
            .attr('y2', (d) => y(d))
            .attr('stroke', '#00000026')
            .attr('stroke-dasharray', '2 2');

        // Tooltip lines and circles for dynamic datasets
        const tooltipLines: Record<string, any> = {};
        const tooltipCircles: Record<string, any> = {};

        Object.keys(chartData).forEach((key) => {
            const { line: color = ReportDefaultColor } = datasetColors[key] || {};

            // Tooltip line
            tooltipLines[key] = svg
                .append('line')
                .attr('stroke', color)
                .attr('stroke-dasharray', '8 8')
                .style('opacity', 0);

            // Tooltip circle
            tooltipCircles[key] = svg
                .append('circle')
                .attr('r', 6)
                .attr('fill', '#FFFFFF')
                .attr('stroke-width', 2)
                .attr('stroke', color)
                .style('opacity', 0);
        });

        const barWidth = width / Math.max(...Object.values(chartData).map((d) => d.length), 1);

        // Add axis lable
        this.addAxisLabel(svg, height, width);

        const isUserReport = this.showAverageSessionChart ? 'selectedSessionUsageUserIds' : 'selectedActiveUserIds';
        // Add hover interaction areas
        svg
            .selectAll('.dot')
            .data(allData)
            .enter()
            .append('rect')
            .attr('class', 'dot')
            .attr('x', (d: any) => x(d.pos))
            .attr('y', 0)
            .attr('width', barWidth)
            .attr('height', height)
            .attr('fill', 'transparent')
            .style('cursor', (d: any) => (
                d.dateOrWeek === undefined ||
                    this[isUserReport].length
                    ? 'default'
                    : 'pointer'))
            .on('mousemove', (d: any) => {

                const centerX = x(d.pos) + barWidth / 60;
                const centerY = y(yMaxValue);

                const dayIndex = d.pos - 1;

                // Ensure activeMonth and activeYear are valid
                const activeMnth = Number(this.activeMonth);
                const month = MONTHS[activeMnth - 1];

                // Construct tooltip data dynamically for all datasets
                this.tooltipData = {
                    date: `${d.label} ${month} ${Number(this.activeYear)}`,
                    datasets: Object.keys(chartData).map((key) => {
                        const dataPoint = chartData[key][dayIndex];
                        const { line: lineColor = ReportDefaultColor } = datasetColors[key] || {};
                        return {
                            label: `${key} Users`, // Adjust labels dynamically
                            value: this.showAverageSessionChart
                                ? dataPoint.avgTimeSpend || 0
                                : dataPoint.value || 0,
                            color: lineColor,
                        };
                    }),
                };

                // Position the tooltip dynamically
                const mouseX = centerX + this.margin.left;
                const mouseY = centerY + this.margin.top;

                // Update tooltip visuals dynamically for each dataset
                Object.keys(chartData).forEach((key) => {
                    const dataset = chartData[key];
                    if (dataset[dayIndex]) {
                        tooltipLines[key]
                            .attr('x1', x(dataset[dayIndex].pos))
                            .attr('x2', x(dataset[dayIndex].pos))
                            .attr('y1', 0)
                            .attr('y2', height)
                            .style('opacity', 1);

                        tooltipCircles[key]
                            .attr('cx', x(dataset[dayIndex].pos))
                            .attr('cy', y(dataset[dayIndex].value))
                            .style('opacity', 1);
                    }
                });

                this.tooltipStyle = {
                    toolX: mouseX + 20,
                    toolY: mouseY - 50,
                };
            })
            .on('mouseleave', () => {
                this.tooltipData = null;
                Object.keys(chartData).forEach((key) => {
                    tooltipLines[key].style('opacity', 0);
                    tooltipCircles[key].style('opacity', 0);
                });
            })
            .on('click', (d: any) => {
                const isUserReportView = this.showAverageSessionChart ? 'selectedSessionUsageUserIds' : 'selectedActiveUserIds';
                if (d.dateOrWeek === undefined || this[isUserReportView].length ) { return; }
                // Emit the event with the clicked data
                this.$emit('onClickChartReport', d,
                    this.showAverageSessionChart
                        ? ReportTypeEnum.SESSION_TIME
                        : ReportTypeEnum.ACTIVE_USERS);
            });
    }


    public groupByUserType() {
        if (!this.dataList || typeof this.dataList !== 'object') {
            return {};
        }
        const groupedByUserType = Object.entries(this.dataList).reduce((acc: any, [userType, data]: [string, any]) => {
            acc[userType] = data.map((current: any) => {
                return this.showAverageSessionChart
                    ? {
                        label: current.label,
                        value: secondsToMinutes(current.avg_session_time_seconds),
                        avgTimeSpend: current.avg_session_time_seconds,
                    }
                    : {
                        label: current.label,
                        value: current.active_user_count,
                    };
            });
            return acc;
        }, {});
        return groupedByUserType;
    }

    public parseChartData(dataList: any[] = []) {
        const dataSet: any[] = [];
        const labelByFrequency = this.frequency === 'daily' ? this.dailyFrequencyLabels : this.weeklyFrequencyLabels;

        labelByFrequency.forEach((frequencyLabel: string | any, index: number) => {
            let coordinateData: any = dataList.find(
                (data: any) => (Number(data.label) === index + 1 || Number(data.label) === frequencyLabel.week));
            const label = this.frequency === 'daily' ? moment(frequencyLabel).format('DD') : frequencyLabel.weekLabel;

            if (coordinateData) {
                coordinateData.dateOrWeek = coordinateData.label;
                coordinateData.label = label;
                coordinateData.pos = index + 1;
                coordinateData.label = coordinateData.label;
                coordinateData.value = Math.max(0, coordinateData.value);
                coordinateData.avgTimeSpend = formatDuration(coordinateData.avgTimeSpend);
            } else {
                coordinateData = {
                    label,
                    pos: index + 1,
                    value: 0,
                    avgTimeSpend: 0,
                };
            }
            dataSet.push(coordinateData);
        });
        return dataSet;
    }

    public displayNoDataMessage(svg: any) {
        // Add "No Data Found" text
        svg.append('text')
            .attr('x', this.chartWidth / 2 - this.margin.left)
            .attr('y', this.chartHeight / 2 - this.margin.top)
            .attr('text-anchor', 'middle')
            .style('font-weight', '600')
            .style('font-family', 'Montserrat')
            .style('text-transform', 'uppercase')
            .style('fill', '#969696')
            .text('No Data Found');
    }

    public generateDatasetColors() {
        return this.reportUserType;
    }

    public addAxisLabel(svg: any, height: number, width: number) {
        const labels = [
            {
                transform: 'translate(-490, 210) rotate(-90)',
                x: 0,
                y: 445,
                text: this.showAverageSessionChart ? 'Mins' : 'Users count',
            },
            {
                transform: 'translate(5, 45) rotate(0)',
                x: width / 2 - 50,
                y: height,
                text: this.frequency === 'daily' ? 'Day' : 'Week',
            },
        ];

        labels.forEach(({ transform, x, y, text }) => {
            svg
                .append('g')
                .attr('transform', transform)
                .append('text')
                .attr('class', 'placeholder')
                .attr('x', x.toString())
                .attr('y', y.toString())
                .style('font-size', '13px')
                .style('font-weight', '600')
                .style('font-family', 'Montserrat')
                .style('text-transform', 'uppercase')
                .style('fill', '#969696')
                .text(text);
        });
    }
}
